import { ChartData, ChartDataset } from "chart.js";
import useSettings from "features/settings/useSettings";
import { createContext, useContext, useMemo } from "react";
import groupReportData from "./groupReportData";
import transformChartData from "./transformChartData";
import {
  SalesReportDataContextType,
  useSalesReportDataContext,
} from "./useSalesReportData";

export type SalesReportParamsType = {
  chartType: "line" | "bar" | "pie";
  value:
    | "amount"
    | "admin_fee_amount"
    | "refund_amount"
    | "refund_guests"
    | "count"
    | "guests"
    | "canceled_amount"
    | "canceled_count"
    | "canceled_guests"
    | "closed_event_count"
    | "program_closed_count"
    | "dynamic_price_diff";
  groupBy: "program" | "program_variant" | "boat" | "partner" | "partner_group";
  dateType: "travel" | "booking";
};

export type GroupedReportData = {
  label: string; // The group label (e.g., partner name, boat name, etc.)
  amount: number; // The summed 'amount' field
  canceled_amount: number; // The summed 'canceled_amount' field
  canceled_count: number; // The summed 'canceled_count' field
  closed_event_count: number; // The summed 'canceled_count' field
  program_closed_count: number;
  dynamic_price_diff: number;
  canceled_guests: number; // The summed 'canceled_guests' field
  refund_amount: number; // The summed 'canceled_guests' field
  refund_guests: number; // The summed 'canceled_guests' field
  count: number; // The summed 'count' field
  guests: number; // The summed 'guests' field
  color: string; // The assigned color for the group
};

// Correct the spelling of 'backgroundColor' in your properties
interface ExtendedChartDataset<T = number[]> extends ChartDataset<"bar", T> {
  total: number;
  totalAmount: number;
  //totalRefundAmount: number;
  totalGuests: number;
  backgroundColor: string; // Ensure the spelling is correct
}

export interface SalesChartData extends ChartData<"bar", number[], string> {
  datasets: ExtendedChartDataset[];
}

export type SalesReportContextType = {
  data: SalesChartData;
  pivot: SalesChartData;
  params: SalesReportParamsType;
  setParams: (filter: SalesReportParamsType) => void;
  load: () => Promise<void>;
  loading: boolean;
} & Omit<SalesReportDataContextType, "data">;

export const SalesReportContext = createContext<SalesReportContextType | null>(
  null,
);

export interface UseSalesReportProps extends Partial<SalesReportParamsType> {}
export default function useSalesReport(props?: UseSalesReportProps) {
  const {
    data,
    filter,
    setFilter,
    load,
    loading,
    excelUrl,
    excelWithBooking,
    setExcelWithBooking,
  } = useSalesReportDataContext();
  /*const [params, setParams] = useState<SalesReportParamsType>({
    chartType: "bar",
    value: "amount",
    groupBy: "program",
    ...props,
  });*/
  const [params, setParams] = useSettings({ key: "salesReport" });

  const transforms = useMemo(() => {
    return {
      data: transformChartData(
        data as any,
        params.groupBy,
        params.chartType,
        filter.from,
        filter.to,
        params.value,
        params.show_canceled,
      ),
      pivot: groupReportData(
        data as any,
        params.groupBy,
        filter.from,
        filter.to,
        params.value,
        params.show_canceled,
      ),
    };
  }, [
    params.value,
    params.chartType,
    params.groupBy,
    params.show_canceled,
    data,
  ]);

  return {
    data: transforms.data as any,
    pivot: transforms.pivot as any,
    filter,
    setFilter,
    params,
    setParams,
    load,
    loading,
    excelUrl,
    excelWithBooking,
    setExcelWithBooking,
  };
}

export function useSalesReportContext() {
  const context = useContext(SalesReportContext);
  if (!context) {
    throw Error(
      "useSalesReportContext cannot be used outside of SalesReportProvider.",
    );
  }
  return context;
}
